"use client";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Card, FormHelperText, Stack, TextField } from "@mui/material";
import { Field, FieldProps, useFormikContext } from "formik";
import React from "react";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { CustomerConfigurationFormData } from "@/components/templates/CustomerForm/types";
import { CustomerFormMessages } from "@/messages/customer/customer-form.messages";

type Props = {
  messages: CustomerFormMessages;
};

type ReminderFieldName = "paymentReminderInterval" | "paymentReminderTimes";

type ReminderFieldProps = {
  name: ReminderFieldName;
  label: string;
  hint: string;
};

function ReminderNumberField({ name, label, hint }: ReminderFieldProps) {
  const { setFieldValue } = useFormikContext<CustomerConfigurationFormData>();

  return (
    <Field name={name}>
      {({ field, meta }: FieldProps) => (
        <MDBox>
          <TextField
            variant="standard"
            name={field.name}
            label={label}
            value={field.value ?? ""}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const onlyNumbers = event.target.value.replace(/[^0-9]/g, "");
              void setFieldValue(name, onlyNumbers, false);
            }}
            onBlur={field.onBlur}
            type="text"
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
            fullWidth
            InputLabelProps={{ shrink: true }}
            error={!!meta.error}
            helperText={meta.error}
            sx={{ mb: 0 }}
          />
          {!meta.error && (
            <FormHelperText
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mt: 0.25,
                mb: 0,
                color: "text.secondary",
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              {hint}
            </FormHelperText>
          )}
        </MDBox>
      )}
    </Field>
  );
}

function DaysUntilDueCard({ messages }: Props) {
  const { values, errors } = useFormikContext<CustomerConfigurationFormData>();

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="h5" fontWeight="bold">
            {messages.daysUntilDueSection}
          </MDTypography>
        </MDBox>

        <MDBox mt={1}>
          <Stack spacing={1.5}>
            <FormField
              name="daysUntilDue"
              type="text"
              label={messages.daysUntilDueLabel}
              variant="standard"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={values.daysUntilDue ?? ""}
              error={!!errors.daysUntilDue}
              sx={{ mb: 0 }}
            />

            <ReminderNumberField
              name="paymentReminderInterval"
              label={messages.paymentReminderIntervalLabel}
              hint={messages.paymentReminderIntervalHint}
            />

            <ReminderNumberField
              name="paymentReminderTimes"
              label={messages.paymentReminderTimesLabel}
              hint={messages.paymentReminderTimesHint}
            />
          </Stack>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default DaysUntilDueCard;
